import React from "react";
import { useSelector } from "react-redux";
import { DriveFileMoveOutlined as FolderIcon } from "@mui/icons-material";
import { Tooltip } from "@mui/material";

import { selectCurrentAccount, useGetMultiProjectQuery } from "fond/api";
import { clearSharedAccount, updateFilter, updateSharedAccount } from "fond/projects/redux";
import { EntityLabel, EntityType, Folder, MultiProject, Project, Report } from "fond/types";
import { useAppDispatch } from "fond/utils/hooks";
import { IconNavLink } from "fond/widgets/Link/IconNavLink";

interface IProps {
  entity: Project | Report | MultiProject | Folder;
}

const getFolderId = (entity: IProps["entity"]): string | null | undefined => {
  switch (entity.EntityType) {
    case "project":
      return entity.FolderID;
    case "folder":
      // the "folder" for a folder is the parent folder.
      return entity.ParentID ?? entity.Parent?.ID;
    default:
      return entity.Folder?.ID;
  }
};

const ViewEntityFolderButton: React.FC<IProps> = ({ entity }) => {
  const dispatch = useAppDispatch();
  const currentAccount = useSelector(selectCurrentAccount);

  const entityType = entity.EntityType;
  const folderId = getFolderId(entity);
  const entityAccountId = entity.Account?.ID;
  const entityInCurrentAccount = currentAccount?.ID === entityAccountId;

  const multiProjectId = entity.EntityType === "report" && entity.MultiProjectArea?.MultiProject?.ID ? entity.MultiProjectArea?.MultiProject?.ID : "";
  const { data: multiProject } = useGetMultiProjectQuery(multiProjectId, { skip: !multiProjectId });

  const getPath = () => {
    if (folderId) {
      if (entityType === "multi_project" || (entityType === "project" && entity.MultiProjectArea !== null)) {
        return `/cities/${folderId}`;
      } else if (entityType === "report" && entity.MultiProjectArea !== null && multiProject?.Folder?.ID) {
        return {
          pathname: `/cities/${multiProject?.Folder?.ID}`,
          search: new URLSearchParams({ multiReportFolderId: folderId }).toString(),
        };
      } else {
        return `/folders/${folderId}`;
      }
    } else {
      const path = entityInCurrentAccount ? "/workspace/" : "/shared/";
      return path;
    }
  };

  const getState = () => {
    if (entityType === "report" && entity.MultiProjectArea !== null && multiProject?.Folder?.ID) {
      return { tab: "report" };
    } else {
      return null;
    }
  };

  const clickHandler = (e: React.MouseEvent) => {
    // If the entity is in the current account, clear the shared account ID
    if (entityInCurrentAccount) {
      dispatch(updateFilter("workspace"));
      dispatch(clearSharedAccount);
    } else {
      dispatch(updateFilter("shared"));
      dispatch(updateSharedAccount(entityAccountId));
    }
  };

  const entityTypeForLabel: EntityType = entityType === "folder" && entity.MultiProject ? "multi_project" : entityType;

  return (
    <Tooltip title={`Go to this ${EntityLabel[entityTypeForLabel]}'s folder`}>
      <IconNavLink to={getPath()} state={getState()} onClick={clickHandler} data-testid={`view-${entityType}-folder-btn`}>
        <FolderIcon fontSize="inherit" />
      </IconNavLink>
    </Tooltip>
  );
};

export default ViewEntityFolderButton;
